/**
 * @fileoverview Trading API Client - specialized client for trading data operations
 * Extends BaseApiClient with trading and market data functionality
 */

import { BaseApiClient } from '../core/BaseApiClient';
import { ApiError } from '../core/errors';
import type { 
  ApiClientConfig, 
  RequestOptions 
} from '../core/types';

// Candle data structure
export interface CandleData {
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  timestamp: string;
}

// Trading-specific types
export interface TradingPair {
  symbol: string;
  baseAsset: string;
  quoteAsset: string;
  status: 'TRADING' | 'BREAK' | 'HALTED';
  minPrice: number;
  maxPrice: number;
  tickSize: number;
  minQty: number;
  maxQty: number;
  stepSize: number;
  minNotional: number;
}

export interface TimeInterval {
  value: string;
  label: string;
  seconds: number;
  maxCandles: number;
}

export interface MarketData {
  symbol: string;
  price: number;
  change24h: number;
  changePercent24h: number;
  volume24h: number;
  high24h: number;
  low24h: number;
  lastUpdate: string;
}

export interface Candlestick {
  openTime: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  closeTime: number;
  trades: number;
}

export interface TradeStats {
  symbol: string;
  timeframe: string;
  startDate: string;
  endDate: string;
  totalTrades: number;
  winningTrades: number;
  losingTrades: number;
  winRate: number;
  avgWin: number;
  avgLoss: number;
  maxWin: number;
  maxLoss: number;
  profitFactor: number;
  totalPnL: number;
  totalVolume: number;
}

export interface EarliestDateResponse {
  symbol: string;
  interval: string;
  earliestDate: string;
  dataAvailable: boolean;
}

export interface SymbolInfo {
  symbol: string;
  name: string;
  baseAsset: string;
  quoteAsset: string;
  category: string;
  sector?: string;
  description?: string;
  website?: string;
  marketCap?: number;
  circulatingSupply?: number;
  totalSupply?: number;
}

export interface MarketOverview {
  totalMarketCap: number;
  total24hVolume: number;
  btcDominance: number;
  ethDominance: number;
  activeMarkets: number;
  fearGreedIndex: number;
  lastUpdate: string;
}

export interface PriceAlert {
  id: string;
  symbol: string;
  type: 'above' | 'below' | 'change';
  targetPrice?: number;
  changePercent?: number;
  isActive: boolean;
  createdAt: string;
  triggeredAt?: string;
}

export interface CreatePriceAlertRequest {
  symbol: string;
  type: 'above' | 'below' | 'change';
  targetPrice?: number;
  changePercent?: number;
}

export class TradingApiClient extends BaseApiClient {
  constructor(config: { baseURL: string } & ApiClientConfig) {
    super(config);
  }

  /**
   * Get all available trading pairs
   */
  async getTradingPairs(options?: RequestOptions): Promise<TradingPair[]> {
    try {
      const response = await this.get<TradingPair[]>('/trading-pairs', options);
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to get trading pairs', 500, 'TRADING_PAIRS_ERROR');
    }
  }

  /**
   * Get available time intervals for charts
   */
  async getTimeIntervals(options?: RequestOptions): Promise<TimeInterval[]> {
    try {
      const response = await this.get<TimeInterval[]>('/intervals', options);
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to get time intervals', 500, 'INTERVALS_ERROR');
    }
  }

  /**
   * Get earliest available data date for symbol and interval
   */
  async getEarliestDate(symbol: string, interval: string, options?: RequestOptions): Promise<EarliestDateResponse> {
    try {
      const response = await this.get<EarliestDateResponse>(
        `/earliest-date/${symbol}/${interval}`,
        options
      );
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to get earliest date', 500, 'EARLIEST_DATE_ERROR');
    }
  }

  /**
   * Get current market data for symbol(s)
   */
  async getMarketData(symbols?: string[], options?: RequestOptions): Promise<MarketData[]> {
    try {
      const params: Record<string, string> = {};
      if (symbols && symbols.length > 0) {
        params.symbols = symbols.join(',');
      }

      const response = await this.get<MarketData[]>('/market-data', { ...options, params });
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to get market data', 500, 'MARKET_DATA_ERROR');
    }
  }

  /**
   * Get historical candlestick data
   */
  async getCandlestickData(
    symbol: string,
    interval: string,
    startDate: string,
    endDate: string,
    options?: RequestOptions
  ): Promise<Candlestick[]> {
    try {
      const params = {
        symbol,
        interval,
        startDate,
        endDate
      };

      const response = await this.get<Candlestick[]>('/candlesticks', { ...options, params });
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to get candlestick data', 500, 'CANDLESTICKS_ERROR');
    }
  }

  /**
   * Get the latest candle for a symbol/interval
   */
  async getLatestCandle(
    symbol: string,
    interval: string,
    options?: RequestOptions
  ): Promise<CandleData | null> {
    try {
      const response = await this.get<{ success: boolean; data: CandleData[] }>(`/candles/${symbol}/${interval}`, { 
        ...options, 
        params: { limit: 1 }
      });
      
      if (response.success && response.data && response.data.length > 0) {
        return response.data[0];
      }
      return null;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to get latest candle', 500, 'LATEST_CANDLE_ERROR');
    }
  }

  /**
   * Get recent candles for a symbol/interval (full history)
   */
  async getRecentCandles(
    symbol: string,
    interval: string,
    limit?: number,
    options?: RequestOptions
  ): Promise<CandleData[]> {
    try {
      // Use candles-full endpoint to get unlimited data
      const params: Record<string, number> = limit ? { maxLimit: limit } : {};
      const response = await this.get<{ success: boolean; data: CandleData[] } | CandleData[]>(`/candles-full/${symbol}/${interval}`, { 
        ...options, 
        params
      });
      
      
      // Handle wrapped response {success: true, data: [...], metadata: {...}}
      if (response && typeof response === 'object' && 'success' in response && 'data' in response) {
        if ('metadata' in response) {
        }
        return response.data || [];
      }
      
      // Handle direct array response
      if (Array.isArray(response)) {
        return response;
      }
      
      return [];
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to get recent candles', 500, 'RECENT_CANDLES_ERROR');
    }
  }

  /**
   * Get available data statistics for all symbols and intervals
   */
  async getCandleStats(options?: RequestOptions): Promise<Array<{ _id: { symbol: string; interval: string }; count: number; minDate: string; maxDate: string }>> {
    try {
      const response = await this.get<any>('/candles-stats', options);
      
      // Handle wrapped response {success: true, data: [...]}
      if (response && typeof response === 'object' && 'success' in response && 'data' in response) {
        return response.data || [];
      }
      
      // Handle direct array response
      if (Array.isArray(response)) {
        return response;
      }
      
      return [];
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to get candle stats', 500, 'CANDLE_STATS_ERROR');
    }
  }

  /**
   * Get trade statistics for a symbol and timeframe
   */
  async getTradeStats(
    symbol: string,
    timeframe: string,
    startDate: string,
    endDate: string,
    options?: RequestOptions
  ): Promise<TradeStats> {
    try {
      const params = {
        symbol,
        timeframe,
        startDate,
        endDate
      };

      const response = await this.get<TradeStats>('/trades/stats', { ...options, params });
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to get trade stats', 500, 'TRADE_STATS_ERROR');
    }
  }

  /**
   * Get symbol information
   */
  async getSymbolInfo(symbol: string, options?: RequestOptions): Promise<SymbolInfo> {
    try {
      const response = await this.get<SymbolInfo>(`/symbols/${symbol}`, options);
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to get symbol info', 500, 'SYMBOL_INFO_ERROR');
    }
  }

  /**
   * Search symbols by name or symbol
   */
  async searchSymbols(query: string, options?: RequestOptions): Promise<SymbolInfo[]> {
    try {
      const params = { q: query };
      const response = await this.get<SymbolInfo[]>('/symbols/search', { ...options, params });
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to search symbols', 500, 'SYMBOL_SEARCH_ERROR');
    }
  }

  /**
   * Get market overview data
   */
  async getMarketOverview(options?: RequestOptions): Promise<MarketOverview> {
    try {
      const response = await this.get<MarketOverview>('/market/overview', options);
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to get market overview', 500, 'MARKET_OVERVIEW_ERROR');
    }
  }

  /**
   * Get user's price alerts
   */
  async getPriceAlerts(options?: RequestOptions): Promise<PriceAlert[]> {
    try {
      const response = await this.get<PriceAlert[]>('/alerts', options);
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to get price alerts', 500, 'PRICE_ALERTS_ERROR');
    }
  }

  /**
   * Create a new price alert
   */
  async createPriceAlert(alertData: CreatePriceAlertRequest, options?: RequestOptions): Promise<PriceAlert> {
    try {
      const response = await this.post<PriceAlert>('/alerts', alertData, options);
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to create price alert', 500, 'CREATE_ALERT_ERROR');
    }
  }

  /**
   * Update a price alert
   */
  async updatePriceAlert(
    alertId: string,
    updates: Partial<CreatePriceAlertRequest>,
    options?: RequestOptions
  ): Promise<PriceAlert> {
    try {
      const response = await this.put<PriceAlert>(`/alerts/${alertId}`, updates, options);
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to update price alert', 500, 'UPDATE_ALERT_ERROR');
    }
  }

  /**
   * Delete a price alert
   */
  async deletePriceAlert(alertId: string, options?: RequestOptions): Promise<void> {
    try {
      await this.delete<void>(`/alerts/${alertId}`, options);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to delete price alert', 500, 'DELETE_ALERT_ERROR');
    }
  }

  /**
   * Toggle price alert on/off
   */
  async togglePriceAlert(alertId: string, isActive: boolean, options?: RequestOptions): Promise<PriceAlert> {
    try {
      const response = await this.patch<PriceAlert>(`/alerts/${alertId}`, { isActive }, options);
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to toggle price alert', 500, 'TOGGLE_ALERT_ERROR');
    }
  }

  /**
   * Get real-time price updates (WebSocket endpoint info)
   */
  async getWebSocketEndpoint(options?: RequestOptions): Promise<{ endpoint: string; token: string }> {
    try {
      const response = await this.get<{ endpoint: string; token: string }>('/realtime/endpoint', options);
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to get WebSocket endpoint', 500, 'WEBSOCKET_ENDPOINT_ERROR');
    }
  }
}
